import { useEffect, useId, useRef, useState } from 'react';
import { FilterDateRange } from '@/features/common/FilterPanel/FilterDateRange';
import { FilterPanel } from '@/features/common/FilterPanel/FilterPanel';
import { FilterSearch } from '@/features/common/FilterPanel/FilterSearch';
import { ActiveFilterTags } from '@/features/publicsite/ActiveFilterTags';
import { listingSearch } from '@/features/publicsite/listingSearch';
import type { ListingFilterPanelProps } from '@/features/publicsite/types/ListingFilterPanelProps';
import { listingFilterPanel } from '@/i18n/dictionaries/listingFilterPanel';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * The filter bar over a public listing — /stiri, /evenimente, /consilieri, /servicii: a search
 * first, the page's own lists, a period last, then "Resetează" — or, where the page passes `tags`,
 * the tags and "Șterge filtrele" under the bar; under it all, how many results a filter left.
 *
 * One component because the two pages are one pattern. The calendar used to hide its lists behind
 * an "Arată filtrele" toggle and keep them out of the URL, so the same kind of page filtered two
 * ways. What differs now is only the lists in the middle (`children`), the card's accessible name
 * and what the page's URL accepts; the search, the period, the buttons and their words are this
 * card's. The words are in its own dictionary rather than handed in by each page: copied into both
 * pages' dictionaries, the search placeholder had already drifted apart between the two.
 *
 * The lists and the period apply the moment one is picked; the search once the reader pauses, or
 * at Enter (listingSearch) — there is no "Caută" to press. A request per keystroke would move the
 * list under the reader's fingers. The count under the bar is the one announcement of the new list,
 * whichever of the three sent it.
 *
 * A page that passes `more` folds those lists behind "Mai multe filtre", and the period goes last
 * among them — unless it passes `periodInRow`, which keeps the period in the open row beside the
 * search; one that passes `tags` gets them under the bar, each with its own ✕, and "Șterge
 * filtrele" beside them in place of "Resetează" — one way to clear it all, not two. A listing with
 * no dates to filter by (/consilieri, /servicii) passes `period={false}`, and one whose search
 * finds something more specific than "words from the title" names it in `searchLabel` and
 * `searchPlaceholder`. Without them the card is the one /stiri has always shown.
 */
export function ListingFilterPanel({
  ariaLabel,
  filters,
  canReset,
  results,
  onFilter,
  onReset,
  children,
  more,
  moreOpen,
  moreCount = 0,
  tags,
  variant,
  period = true,
  periodInRow = false,
  searchLabel,
  searchPlaceholder,
}: ListingFilterPanelProps) {
  const { t } = useTranslation(listingFilterPanel);
  const searchId = useId();
  // The text box follows the URL rather than owning its value, so Back and a shared link show the
  // search that produced the list — but it keeps what is being typed until it is sent, including
  // the trailing space of a word about to be followed by another, which the URL trims away.
  const urlQuery = filters.q ?? '';
  const [query, setQuery] = useState(urlQuery);
  useEffect(() => setQuery((typed) => (typed.trim() === urlQuery ? typed : urlQuery)), [urlQuery]);

  // The latest onFilter for a search that goes out after a pause: the page's closes over the URL
  // it was rendered with, and the pause outlives that render.
  const onFilterRef = useRef(onFilter);
  useEffect(() => {
    onFilterRef.current = onFilter;
  });
  const [search] = useState(() =>
    listingSearch((q, navigation) => onFilterRef.current({ q }, navigation)),
  );
  useEffect(() => search.typed(query, urlQuery), [search, query, urlQuery]);
  useEffect(() => search.cancel, [search]);

  // Clearing drops the search still waiting on its pause, and the text it was about to send: when
  // the URL held no search, clearing does not change it, so nothing else would stop the pause
  // from sending the old words over the list just emptied.
  const reset = () => {
    search.cancel();
    setQuery('');
    onReset();
  };

  // Where the period goes. With the folded fields when a page has any, since it is the least
  // used of them — except where the page says the period is one of the two things it opens on
  // (/evenimente, whose other four filters all fold). A page with nothing folded has it in the
  // row either way.
  const periodFolds = Boolean(more) && !periodInRow;

  const periodField = period && (
    <FilterDateRange
      name="period"
      label={t('period')}
      ariaLabel={t('periodAria')}
      from={filters.from ?? null}
      to={filters.to ?? null}
      onChange={(from, to) => onFilter({ from, to })}
    />
  );

  return (
    <>
      <FilterPanel
        className="listing-filters"
        variant={variant}
        layout="grid"
        ariaLabel={ariaLabel}
        onSubmit={() => search.submitted(query)}
        resetLabel={tags ? undefined : t('reset')}
        canReset={tags ? undefined : canReset}
        onReset={tags ? undefined : reset}
        more={
          more && (
            <>
              {more}
              {periodFolds && periodField}
            </>
          )
        }
        // Only while folded, and only the lists behind it: the button is there to say that
        // something out of sight is narrowing the list. Open, they are on screen to be read.
        moreLabel={moreCount > 0 ? t('moreFiltersCount', { count: moreCount }) : t('moreFilters')}
        lessLabel={t('fewerFilters')}
        moreOpen={moreOpen}
      >
        <FilterSearch
          id={searchId}
          label={searchLabel ?? t('search')}
          placeholder={searchPlaceholder ?? t('searchPlaceholder')}
          value={query}
          onChange={setQuery}
        />
        {children}
        {!periodFolds && periodField}
      </FilterPanel>
      {tags && (
        <ActiveFilterTags
          tags={tags}
          canClear={canReset}
          onClear={reset}
          fallbackFocusId={searchId}
        />
      )}
      {/* Only while filtering: unfiltered, the count is just the size of the archive, which the
          reader did not ask about. Polite, so a screen reader hears how many a filter left without
          being interrupted. */}
      <p className="listing-results" aria-live="polite">
        {results}
      </p>
    </>
  );
}
